import type { StaffPermission, StaffRole } from '@traceio-org/platform-contracts';

/**
 * What Trace Admin offers a staff member. It only shapes the screens (a support viewer sees no edit
 * control); platform-api checks the same permissions again on every request and stays the authority.
 * The abilities come from the permissions GET /v1/admin/me returned (STAFF_ROLE_PERMISSIONS on the
 * server), never from the role name alone, so the UI cannot offer more than the server granted.
 */
export interface StaffAbilities {
  /** Enable, disable and configure modules; start and release emergency stops (staff.orgs.manage). */
  configureModules: boolean;
  /** Change a module's rollout per environment (staff.orgs.manage: platform-wide, platform admins only). */
  manageReleaseFlags: boolean;
  /** Link or sync an organization's Auth0 organization, which product sign-in needs (staff.orgs.manage). */
  linkAuth0Organization: boolean;
  /**
   * Create an organization (New organization) and retry the Enterprise provisioning it starts (staff.orgs.manage:
   * platform admins only).
   */
  createOrganizations: boolean;
  /** Add trials and support grants (staff.billing.manage). */
  grantEntitlements: boolean;
  /** Add and end an organization's product subscriptions (staff.billing.manage: the commercial layer). */
  manageSubscriptions: boolean;
  /** See and change Trace staff (staff.orgs.manage, which only platform admins hold). */
  manageStaff: boolean;
  /** Create and change an organization's portals and portal customer accounts (staff.orgs.manage). */
  managePortals: boolean;
  /** Record a key's expiry and choose its provider on the Keys page (staff.orgs.manage: platform admins only). */
  manageKeys: boolean;
  /** Restart, redeploy, stop and start App Platform apps on the Apps pages (staff.orgs.manage: platform admins only). */
  operateApps: boolean;
  /** Edit, suspend and remove members; create and revoke invitations (staff.members.manage). */
  manageMembers: boolean;
  /** Add, verify, disable, enable and release custom domains (staff.domains.manage). */
  manageDomains: boolean;
  /** Request time-limited support access to an organization's data (staff.support_access.request). */
  requestSupportAccess: boolean;
  /** Read the audit log (staff.audit.read). */
  readAudit: boolean;
  /**
   * Read billing: the Billing area (plan versions) and the Billing section of an organization (contracts, usage,
   * line items, adjustments, documents). staff.billing.read, or staff.billing.manage, which implies it: a change
   * is never offered on a screen that cannot be read.
   */
  readBilling: boolean;
  /**
   * Change billing: draft, edit and publish plan versions, sign and change contracts, request and decide
   * adjustments (staff.billing.manage).
   */
  manageBilling: boolean;
  /** See Trace's ticket queue and tickets: the Tickets section and an organization's Tickets tab (staff.tickets.read). */
  readTickets: boolean;
  /** Reply to customers and write Trace notes (staff.tickets.reply). */
  replyTickets: boolean;
  /**
   * Work tickets: status, priority, assignee, labels, links, hand back, and filing a ticket for a customer
   * (staff.tickets.manage).
   */
  manageTickets: boolean;
  /** Create, rename, recolour, regroup and archive ticket labels (staff.tickets.labels.manage). */
  manageTicketLabels: boolean;
  /**
   * Change the response targets: PUT /v1/admin/ticket-settings enforces staff.tickets.labels.manage (the ticket
   * catalogue permission), so this ability maps to the same one under its own name.
   */
  manageTicketSettings: boolean;
  /** Set an organization's ticket code (staff.orgs.manage). */
  manageTicketCode: boolean;
  /**
   * Platform admins read an organization's data beyond its metadata without a support access grant; every
   * other staff role needs an active grant. Used only to explain a refusal, never to decide anything.
   */
  readsWithoutGrant: boolean;
}

const ABILITY_PERMISSIONS: Readonly<
  Record<Exclude<keyof StaffAbilities, 'readsWithoutGrant' | 'readBilling'>, StaffPermission>
> = {
  configureModules: 'staff.orgs.manage',
  manageReleaseFlags: 'staff.orgs.manage',
  linkAuth0Organization: 'staff.orgs.manage',
  createOrganizations: 'staff.orgs.manage',
  grantEntitlements: 'staff.billing.manage',
  manageSubscriptions: 'staff.billing.manage',
  manageStaff: 'staff.orgs.manage',
  managePortals: 'staff.orgs.manage',
  manageKeys: 'staff.orgs.manage',
  operateApps: 'staff.orgs.manage',
  manageMembers: 'staff.members.manage',
  manageDomains: 'staff.domains.manage',
  requestSupportAccess: 'staff.support_access.request',
  readAudit: 'staff.audit.read',
  manageBilling: 'staff.billing.manage',
  readTickets: 'staff.tickets.read',
  replyTickets: 'staff.tickets.reply',
  manageTickets: 'staff.tickets.manage',
  manageTicketLabels: 'staff.tickets.labels.manage',
  manageTicketSettings: 'staff.tickets.labels.manage',
  manageTicketCode: 'staff.orgs.manage',
};

export function staffAbilities(context: {
  staffRole: StaffRole;
  permissions: readonly string[];
}): StaffAbilities {
  const has = (permission: StaffPermission) => context.permissions.includes(permission);
  const manageBilling = has(ABILITY_PERMISSIONS.manageBilling);
  return {
    configureModules: has(ABILITY_PERMISSIONS.configureModules),
    manageReleaseFlags: has(ABILITY_PERMISSIONS.manageReleaseFlags),
    linkAuth0Organization: has(ABILITY_PERMISSIONS.linkAuth0Organization),
    createOrganizations: has(ABILITY_PERMISSIONS.createOrganizations),
    grantEntitlements: has(ABILITY_PERMISSIONS.grantEntitlements),
    manageSubscriptions: has(ABILITY_PERMISSIONS.manageSubscriptions),
    manageStaff: has(ABILITY_PERMISSIONS.manageStaff),
    managePortals: has(ABILITY_PERMISSIONS.managePortals),
    manageKeys: has(ABILITY_PERMISSIONS.manageKeys),
    operateApps: has(ABILITY_PERMISSIONS.operateApps),
    manageMembers: has(ABILITY_PERMISSIONS.manageMembers),
    manageDomains: has(ABILITY_PERMISSIONS.manageDomains),
    requestSupportAccess: has(ABILITY_PERMISSIONS.requestSupportAccess),
    readAudit: has(ABILITY_PERMISSIONS.readAudit),
    readBilling: has('staff.billing.read') || manageBilling,
    manageBilling,
    readTickets: has(ABILITY_PERMISSIONS.readTickets),
    replyTickets: has(ABILITY_PERMISSIONS.replyTickets),
    manageTickets: has(ABILITY_PERMISSIONS.manageTickets),
    manageTicketLabels: has(ABILITY_PERMISSIONS.manageTicketLabels),
    manageTicketSettings: has(ABILITY_PERMISSIONS.manageTicketSettings),
    manageTicketCode: has(ABILITY_PERMISSIONS.manageTicketCode),
    readsWithoutGrant: context.staffRole === 'platform_admin',
  };
}
